import i18n from 'i18next';
import moment from 'moment';
import { Table, Col, Row, Button, Tag, Space, Tooltip } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, QuestionCircleOutlined, EditFilled, DeleteFilled } from '@ant-design/icons';
import { Permission } from '../../features/user/Permission';
import { BUS_TYPES, CALCULATABLE_MEASUREMENTS, ENGINE_HOURS_IDENTIFIER, FeatureMeasurementTable, getStatusConfig, SOURCE_INTERNAL, SOURCE_SHERLOCK, STATUS_KEY_MAP, VERIFICATION_STATUS } from './constant';
import { Buttons } from '../../components/control/Buttons';
import { Can } from '../../features/user/Can';
import { ResponsiveButton } from '../../features/responsive';

const statusColors = {
	IN_REVIEW: 'blue',
	APPROVED: 'green',
	DECLINED: 'red'
  };

export const getExportOptionButtons = ({
	handleExportExcel,
	handleExportCSV,
	handleAddVehicleModel,
	can,
	isLoading = false,
	dataSize
}) => {
	return [
		can({ everyPermission: Permission.COMMON_EDIT }) && {
			name: i18n.t('VehicleModel.AddVehicleModel'),
			onClick: handleAddVehicleModel,
			disabled:isLoading,
			value: 'btn_addVehicleModel'
		},
		can({ everyPermission: Permission.EXPORT }) && {
			name: i18n.t('Common.ExportToCSV'),
			onClick: handleExportCSV,
			disabled:isLoading || dataSize === 0,
			value: 'btn_exportCsv'
		},
		can({ everyPermission: Permission.EXPORT }) && {
			name: i18n.t('Common.ExportToExcel'),
			onClick: handleExportExcel,
			disabled:isLoading || dataSize === 0,
			value: 'btn_exportExcel'
		}
	].filter(i=>!!i);
};

const renderKit = (kitDetail, onViewKit, vehicleModel, viewable = true) =>{
	return (
		<Row className="mb-1">
		{
			kitDetail.map(data => {
				return (
					<Col xs={{ span: 24 }}>
						{viewable ? <Button onClick={() => onViewKit(data, vehicleModel)} type="link">{data?.sapCode || "-"}</Button> : <div>{data?.sapCode || "-"}</div>}
					</Col>
				)
			})
		}
		</Row>
	)
}

export const VehicleMeasurementDataColumns = [
	{
		title: i18n.t('VehicleMeasurement.DeviceModel'),
		sorter: (a, b) => a.deviceModelName.localeCompare(b.deviceModelName),
		render: (e, r) => <div>{r.deviceModelName}</div>
	},
	{
		title: i18n.t('VehicleMeasurement.BusType'),
		sorter: (a, b) => a.busType.localeCompare(b.busType),
		render: (e, r) => <div>{r.busType}</div>
	},
	{
		title: i18n.t('VehicleMeasurement.Pid'),
		sorter: (a, b) => a.pid.localeCompare(b.pid),
		render: (e, r) => <div>{r.pid}</div>
	},
	{
		title: i18n.t('VehicleMeasurement.Description'),
		sorter: (a, b) => a.description.localeCompare(b.description),
		render: (e, r) => <div>{r.description}</div>
	},
	{
		title: i18n.t('VehicleMeasurement.MeasurementId'),
		render: (e, r) => <div>{r.extras?.id || "-"}</div>
	},
	{
		title: i18n.t('VehicleMeasurement.Unit'),
		render: (e, r) => <div>{r.extras?.unit || "-"}</div>
	},
	{
		title: i18n.t('VehicleMeasurement.ValueTable'),
		render: (e, r) => <div>{r.extras?.value || "-"}</div>
	},
	{
		title: i18n.t('VehicleMeasurement.Note'),
		render: (e, r) => <div>{r.extras?.note || "-"}</div>
	},
	{
		title: i18n.t('VehicleMeasurement.Category'),
		sorter: (a, b) => a.category.localeCompare(b.category),
		render: (e, r) => <div>{r.category}</div>
	}
];

export const getVehicleModelCleanupDataColumns = (action) => [
	{
		title: i18n.t('VehicleModel.Make'),
		sorter: (a, b) => a.make.localeCompare(b.make),
		render: (e, r) => <div>{r.make}</div>
	},
	{
		title: i18n.t('VehicleModel.Model'),
		sorter: (a, b) => a.model.localeCompare(b.model),
		render: (e, r) => <div>{r.model}</div>
	},
	{
		title: i18n.t('VehicleModel.Year'),
		sorter: (a, b) => (a.year?.toString() || "0").localeCompare(b.year?.toString()  || "0"),
		render: (e, r) => <div>{r.year}</div>
	},
	{
		title: i18n.t('VehicleModel.ExpectedMakeValue'),
		sorter: (a, b) => a.expectedMake?.localeCompare(b.expectedMake),
		render: (e, r) => <div>{r.expectedMake}</div>,
		responsive: ['md']
	},
	{
		align:"center",
		render: (e, r) => {
			let actions = [
				{
					name: i18n.t('Audits.Audits'),
					onClick: () => {
						if (action.audit)
							action.audit(r)
					},
					permission: [Permission.AUDIT]
				},
				{
					name: i18n.t('VehicleModel.RenameMake'),
					onClick: () => {
						if (action.renameVehicleModel)
							action.renameVehicleModel(r)
					},
					permission: [Permission.COMMON_EDIT]
				},
				{
					name: i18n.t('VehicleModel.MarkVerified'),
					onClick: () => {
						if (action.markVerified)
							action.markVerified(r)
					},
					permission: [Permission.COMMON_EDIT]
				}
			];

			if ((r.groupId === undefined || r.groupId === null) && !r.isDummyInternal) {
				actions.push(
					{
						name: i18n.t('VehicleModel.Merge'),
						onClick: () => {
							if (action.mergeVehicleModel)
								action.mergeVehicleModel(r)
						},
						permission: [Permission.MERGE_VEHICLE_MODEL]
					});
			}
			return (<Can oneOfPermission={[Permission.COMMON_EDIT, Permission.MERGE_VEHICLE_MODEL]}>
				<Buttons
					noMargin={true}
					moreButtons={actions}
				/>
			</Can>
			)
		}
	}
];

export const getVehicleModelScanningRequestColumns = (action) => [
	{
		title: i18n.t('VehicleModel.Make'),
		sorter: (a, b) => a.vehicleModel.make.localeCompare(b.vehicleModel.make),
		render: (e, r) => <div>{r.vehicleModel.make}</div>
	},
	{
		title: i18n.t('VehicleModel.Model'),
		sorter: (a, b) => a.vehicleModel.model.localeCompare(b.vehicleModel.model),
		render: (e, r) => <div>{r.vehicleModel.model}</div>
	},
	{
		title: i18n.t('VehicleModel.Year'),
		sorter: (a, b) => (a.vehicleModel.yearOfManufacture?.toString() || "0").localeCompare(b.vehicleModel.yearOfManufacture?.toString()  || "0"),
		render: (e, r) => <div>{r.vehicleModel.yearOfManufacture}</div>
	},
	{
		title: i18n.t('VehicleModel.ScanningRequestCount'),
		sorter: (a, b) => (a.count.yearOfManufacture?.toString() || "0").localeCompare(b.count?.toString()  || "0"),
		render: (e, r) => <div>{r.count}</div>
	},
	{
		title: i18n.t('VehicleModel.ScanningRequestStatus'),
		sorter: (a, b) => a.status?.localeCompare(b.status),
		render: (e, r) => <Tag color={statusColors[r.status]}>{i18n.t('VehicleModel.RequestStatus.' + r.status)}</Tag>
	},
	{
		title: i18n.t('VehicleModel.Comment'),
		sorter: (a, b) => a.comment?.localeCompare(b.comment),
		render: (e, r) => <div>{r.comment}</div>,
		responsive: ['md']
	},
	{
		align:"center",
		render: (e, r) => {
			let actions = r.status === 'IN_REVIEW' ? [
				{
					name: i18n.t('VehicleModel.Approve'),
					onClick: () => {
						if (action.approve)
							action.approve(r)
					},
					permission: [Permission.COMMON_EDIT]
				},
				{
					name: i18n.t('VehicleModel.Decline'),
					onClick: () => {
						if (action.decline)
							action.decline(r)
					},
					permission: [Permission.COMMON_EDIT]
				},
				{
					name: i18n.t('Audits.Audits'),
					onClick: () => {
						if (action.audit)
							action.audit(r)
					},
					permission: [Permission.AUDIT]
				}
			] : [
				{
					name: i18n.t('Audits.Audits'),
					onClick: () => {
						if (action.audit)
							action.audit(r)
					},
					permission: [Permission.AUDIT]
				}
			];
			return (<Can oneOfPermission={[Permission.COMMON_EDIT]}>
				<Buttons
					noMargin={true}
					moreButtons={actions}
				/>
			</Can>);
		}
	}
];

export const getVehicleModelPollHistoryColumns = (localization, action) => [
	{
		title: i18n.t('VehicleModel.Make'),
		sorter: (a, b) => a.vehicleModel.make.localeCompare(b.vehicleModel.make),
		render: (e, r) => <div>{r.vehicleModel.make}</div>
	},
	{
		title: i18n.t('VehicleModel.Model'),
		sorter: (a, b) => a.vehicleModel.model.localeCompare(b.vehicleModel.model),
		render: (e, r) => <div>{r.vehicleModel.model}</div>
	},
	{
		title: i18n.t('VehicleModel.Year'),
		sorter: (a, b) => (a.vehicleModel.yearOfManufacture?.toString() || "0").localeCompare(b.vehicleModel.yearOfManufacture?.toString()  || "0"),
		render: (e, r) => <div>{r.vehicleModel.yearOfManufacture}</div>
	},
	{
		title: i18n.t('VehicleModel.RequestedDate'),
		render: (e, r) => <div>{moment(r.createdAt).format(localization.convertTimeFormat("DD MMM YYYY HH:mm:ss"))}</div>,
		responsive: ['md']
	},
	{
		title: i18n.t('VehicleModel.ScanningRequestStatus'),
		render: (e, r) => <Tag color={statusColors[r.status]}>{i18n.t('VehicleModel.RequestStatus.' + r.status)}</Tag>
	},
	{
		title: i18n.t('VehicleModel.Comment'),
		render: (e, r) => <div>{r.comment}</div>,
		responsive: ['md']
	},
	{
		align:"center",
		render: (e, r) => {
			let actions = [
				{
					name: i18n.t('VehicleModel.CancelRequest'),
					onClick: () => {
						if (action.cancelRequest)
							action.cancelRequest(r)
					}
				}
			];
			return r.status === 'IN_REVIEW' ? (
				<Can oneOfPermission={[Permission.COMMON_EDIT]}>
					<Buttons
						noMargin={true}
						moreButtons={actions}
					/>
				</Can>) : null;
		}
	}
];

export const getExportFilename = ({ entityName, extension }) => {
	if (!entityName) {
		return;
	}
	return `${entityName}${extension}`;
};

export const CsvSearchTemplateHeader = [
	[
		i18n.t("VehicleModel.Make")+"*",
		i18n.t("VehicleModel.Model")+"*",
		i18n.t("VehicleModel.Year") + " ("+ i18n.t("VehicleModel.CsvSearchHint2")+")"
	]
];

export const getVehicleModelDataColumns = (companies) => [
	{
		title: "",
		width:"2%"
	},
	Table.EXPAND_COLUMN,
	{
		title: "",
		width:"3%"
	},
	{
		title: i18n.t('VehicleModel.Make'),
		sorter: (a, b) => a.make.localeCompare(b.make),
		render: (e, r) => <div>{r.make}</div>
	},
	{
		title: i18n.t('VehicleModel.Model'),
		sorter: (a, b) => a.model.localeCompare(b.model),
		render: (e, r) => <div>{r.model}</div>
	},
	{
		title: i18n.t('VehicleModel.Year'),
		sorter: (a, b) => (a.yearOfManufacture?.toString() || "0").localeCompare(b.yearOfManufacture?.toString()  || "0"),
		render: (e, r) => {
			const uniqueYears = new Set( r.yearList || []);
			if (uniqueYears.size > 1) {
				const sortedNumbers = Array.from(uniqueYears).sort((a, b) => a - b);
	
				if (isSequentialList(sortedNumbers)) {
					const smallest = sortedNumbers[0];
					const largest = sortedNumbers[sortedNumbers.length - 1];
					return (<div>{smallest + " - " + largest}</div>);
				}
				else {
					return <div>{sortedNumbers.join(', ')}</div>
				}
			}
			else {
				return (<div>{r.yearOfManufacture}</div>)
			}
		}
	},
	{
		title: i18n.t('VehicleModel.Company'),
		width:"15%",
		responsive: ['lg'],
		render: (e, r) => <div>{(companies || []).find(i=>i.id === r.company?.id)?.name || i18n.t('VehicleModel.GlobalModel')}</div>,
	},
	{
		title: i18n.t('VehicleModel.SupportStatus'),
		align:"right",
		responsive: ['md'],
		render: (e, r) => <div style={{ display: 'flex', justifyContent: "center", alignItems: "center", whiteSpace: "nowrap" }}>
			<Tooltip title={getSupportStatusToolTip(r)}>{r.isSupported === undefined ?
				<><QuestionCircleOutlined />&nbsp;&nbsp;{i18n.t("VehicleModel.Unknown")}</> :
				(r.isSupported ? <><CheckCircleOutlined style={{ color: "green" }} />&nbsp;&nbsp;{i18n.t("VehicleModel.Supported")}</> :
					<><CloseCircleOutlined style={{ color: "red" }} />&nbsp;&nbsp;{i18n.t("VehicleModel.Unsupported")}</>)}
			</Tooltip>
		</div>,
	},
	{
		title: "",
		width:"45%",
		responsive: ['lg']
	}
];

export const getVehicleModelDataColumnsForUpdate = (onEdit, onDelete, customAction) => [
	{
		title: i18n.t('VehicleModel.Make'),
		sorter: (a, b) => a.make.localeCompare(b.make),
		render: (e, r) => <div>{r.make}</div>
	},
	{
		title: i18n.t('VehicleModel.Model'),
		sorter: (a, b) => a.model.localeCompare(b.model),
		render: (e, r) => <div>{r.model}</div>
	},
	{
		title: i18n.t('VehicleModel.Year'),
		sorter: (a, b) => (a.yearOfManufacture?.toString() || "0").localeCompare(b.yearOfManufacture?.toString() || "0"),
		render: (e, r) => <div>{r.yearOfManufacture}</div>
	},
	{
		title: "",
		width: "55%",
		align: "right",
		render: (e, r) => {
			return <Space >
				<ResponsiveButton onClick={() => onEdit(r)}
					size={"small"} title={i18n.t("Common.Edit")} icon={<EditFilled />} type={"text"} />
				{onDelete && <Tooltip title={r.canDelete ? "" : i18n.t("VehicleModel.NotEdittable")}>
					<>
						<ResponsiveButton disabled={!r.canDelete} onClick={() => onDelete(r)} size={"small"}
							title={i18n.t("Common.Delete")} icon={<DeleteFilled />} type={"text"} />
					</>
				</Tooltip>}
				{customAction && customAction(r)}
			</Space>
		}
	}
];

export const isSequentialList = (arr) => {
    if (arr.length === 0) return false;

    const sortedList = [...arr].sort((a, b) => a - b);
    for (let i = 1; i < sortedList.length; i++) {
      if (sortedList[i] - sortedList[i - 1] !== 1) {
        return false;
      }
    }
    return true;
  };


  export const getMMYCombination = (filter, vehicleCompatibility, dataPoolIdentifiers) => {
	const yearValue = filter?.year || [];
	const makeValue = filter?.make || [];
	const modelValue = filter?.model || [];
	
	let makeList = [];
	let modelList = [];
	let yearList = [];

	let filteredData = vehicleCompatibility || [];

	filteredData.map(makeModelYear => {
		const makeMatch= (makeValue?.length === 0 || makeValue?.includes(makeModelYear.make));
		const modelMatch=(modelValue?.length === 0 || modelValue?.includes(makeModelYear.model));
		const yearMatch= (yearValue?.length === 0 || yearValue?.includes(makeModelYear.yearOfManufacture));

		if ((makeMatch && yearMatch) || (modelValue?.includes(makeModelYear.model))) {
			modelList.push(makeModelYear.model);
		}

		if ((modelMatch && yearMatch) || (makeValue?.includes(makeModelYear.make))) {
			makeList.push(makeModelYear.make);
		}

		if ((modelMatch && makeMatch) || (yearValue?.includes(makeModelYear.yearOfManufacture))) {
			if (makeModelYear.yearOfManufacture) {
				yearList.push(makeModelYear.yearOfManufacture);
			}

			if (makeModelYear.fullGroupModels) {
				makeModelYear.fullGroupModels.filter(vm=>!!vm.yearOfManufacture).forEach(vm=>{
					yearList.push(vm.yearOfManufacture);
				})
			}
		}
	});

	yearList =  Array.from(new Set(yearList.map(i=>i))).map(i=>{
		return { label: i?.toString(), value: i }
	});

	modelList =  Array.from(new Set(modelList.map(i=>i?.trim()))).map(i=>{
		return { label: i, value: i }
	})

	makeList  =Array.from(new Set(makeList.map(i=>i?.trim()))).map(i=>{
		return { label: i, value: i }
	});

	const supportList  =[
		{ label: i18n.t("VehicleModel.Supported"), value: true },
		{ label: i18n.t("VehicleModel.Unsupported"), value: false },
		{ label: i18n.t("VehicleModel.Unknown"), value: -1 }
	];

	const verificationStatus = getStatusConfig();
	const verificationList = Object.keys(verificationStatus).map(d=>{
		return {
			label:i18n.t(verificationStatus[d].label),
			value:d,
			role:verificationStatus[d].role,
		}
	});

	const combinedDataPoolIdentifier = Object.values(dataPoolIdentifiers || {}).reduce((acc, inner) => {
	    return { ...acc, ...inner };
	}, {});

	const measurements = Object.keys(combinedDataPoolIdentifier).map(d=>{
		return {
			label:combinedDataPoolIdentifier[d],
			value:d,
		}
	});

	return {
		make : makeList,
		model : modelList,
		year : yearList,
		supportList : supportList,
		measurements : measurements,
		verificationList : verificationList
	}
};

export const getSourceToolTip = (source) => {
	if (source === "internal") {
		return i18n.t("VehicleModel.InternalSourceHint");
	}
	else if (source === "sherlock" || source === "inventure") {
		return i18n.t("VehicleModel.InventureSourceHint");
	}
}

export const getSupportStatusToolTip = (r) => {
	if (r.isSupported === true) {
		return i18n.t("VehicleModel.SupportedStatusHint");
	}
	else if (r.isSupported === false) {
		let customNotSupportHint = i18n.t("VehicleModel.UnsupportedStatusHint");
		if (r.extras) {
			customNotSupportHint = r.extras.reason || customNotSupportHint;
		}

		return customNotSupportHint;
	}
	else {
		return i18n.t("VehicleModel.UnknownSupportedStatusHint");
	}
}

export const getDisplaySource = (source) => {
  if (source === "sherlock" || source === "inventure") {
    return SOURCE_SHERLOCK;
  } else if (source === 'internal') {
    return SOURCE_INTERNAL;
  } else  {
    return source;
  }
}

export const getBusType = (busType) => {
	return busType?.includes('/') ? busType?.split('/').pop() : busType?.split('_').pop();
}

export const formatYearList = (yearList) => {
	const uniqueYears = Array.from(new Set(yearList || []));
	if (uniqueYears.length <= 1) {
		return uniqueYears[0];
	} else {
		const sortedYears = uniqueYears.sort((a, b) => a - b);
		const isSequential = sortedYears.every((year, i) => i === 0 || year === sortedYears[i - 1] + 1);

		if (isSequential) {
			return `${sortedYears[0]} - ${sortedYears[sortedYears.length - 1]}`;
		} else {
			return sortedYears.join(', ');
		}
	}
}

export const getFeatureMeasurement = (input) => {
  return Object.keys(FeatureMeasurementTable).map(key => ({
    key: key,
    supported: FeatureMeasurementTable[key].every(group =>
      group.some(v => input.includes(v))
    )
  }));
}

export const getBusTypeFromVin = (vin) => {
  if (vin.toUpperCase().endsWith('OBDII')) return BUS_TYPES.OBDII;
  if (vin.toUpperCase().endsWith('J1939')) return BUS_TYPES.J1939;
  if (vin.toUpperCase().endsWith('J1708')) return BUS_TYPES.J1708;
  const parts = vin.split('_');

  if (parts.length >= 2 && parts[0] !== "internal") {
    return parts[0];
  }

  return BUS_TYPES.DEFAULT;
};

const getStatusFromPidDetail = (pid = {}, statusConfig, model) => {
  const { verificationStatus, dataPoolIdentifiers } = pid;
  if (['V', 'P'].includes(verificationStatus)) return statusConfig.VERIFIED;
  if (verificationStatus === 'N') return statusConfig.NOT_SUPPORTED;
  if (verificationStatus === 'U' || (dataPoolIdentifiers || []).length !== 0) {
    if (model?.source?.toLowerCase() !== 'internal') {
      return statusConfig.SUPPORTED;
    }
    else {
      return model?.pidVerificationStatus ? statusConfig.NOT_SUPPORTED : statusConfig.UNKNOWN;
    }
  }

  return null;
};

export const resolveMeasurementStatus = (dataPoolIdentifier, modelPids, busType, statusConfig, model) => {
  const pid = modelPids.find(p => p.dataPoolIdentifiers?.includes(dataPoolIdentifier.key));

  let status =
    getStatusFromPidDetail(pid, statusConfig, model)
    ?? (busType === BUS_TYPES.DEFAULT
      ? statusConfig.UNKNOWN
      : statusConfig.NOT_SUPPORTED);

  let calculated = false;

  // Skip calculation check when busType is DEFAULT
  if (busType === BUS_TYPES.DEFAULT) {
    return { status, calculated };
  }

  // If it's still neither VERIFIED nor SUPPORTED, see if we can "calculate" it
  if (statusConfig.VERIFIED !== status) {
    // a) special case: engine hours is always SUPPORTED when missing
    if (dataPoolIdentifier.key === ENGINE_HOURS_IDENTIFIER) { 
      const emptyVcmCase = model?.source?.toLowerCase() !== 'internal' ? statusConfig.SUPPORTED : statusConfig.UNKNOWN;
      status = model?.pidVerificationStatus === undefined ? emptyVcmCase : statusConfig.VERIFIED;

      calculated = model?.pidVerificationStatus === undefined ? pid === undefined : true;
    }
    // b) other calculatables: look up the alias PIDs in CALCULATABLE_MEASUREMENTS
    else if (statusConfig.SUPPORTED !== status) {
      const aliases = CALCULATABLE_MEASUREMENTS[dataPoolIdentifier.key];
      const statusKey = Object.keys(STATUS_KEY_MAP);
      if (aliases) {
        //find all the mapping measurement and check the status based on status order V U N P X, so the first mapped status will be used
        const calcPids = modelPids.filter(p => p.dataPoolIdentifiers?.some(id => aliases.includes(id)));
        const calcPid = statusKey.map(status => calcPids.find(item => item.verificationStatus === status)).find(Boolean) || calcPids[0]

        const calcStatus = getStatusFromPidDetail(calcPid, statusConfig, model);
        if (calcStatus) {
          status = calcStatus;
          calculated = statusConfig.UNKNOWN !== calcStatus && statusConfig.NOT_SUPPORTED !== calcStatus;
        }
      }
    }
  }

  return { status, calculated };
};